import React, { useState } from 'react';
import { Spin, Tooltip, Form, Tag, Switch } from 'antd';
import dayjs from 'dayjs';
import { SearchOutlined, CheckCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { T, H_TITLE, SECTION_LABEL } from './declarationTheme';
import { libellePaysOrganisme } from '../../../variables/paysCedeao';
import { Field, Grid2, Grid3, Input, DatePicker, Select, inputProps, selectProps } from './fields';
import axiosInstance from '../../../variables/api/axiosInstance';

/* ─────────────────────────────────────────────────────────────
   Étape 2 - Contrat & Assureurs
   - N° de police, N° attestation CEDEAO (avec lookup Assuré existant)
   - Assureur principal (COMPAGNIE_MEMBRE) + assureurs secondaires
   - Dates d'effet / échéance
   ───────────────────────────────────────────────────────────── */
export default function StepContratAssureurs({ form, organismes = [], pays = [], embedded = false }) {
  const { t } = useTranslation('declaration');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(false);
  const attestationIllisible = Form.useWatch('attestationIllisible', form);

  const typeSinistre = Form.useWatch('typeSinistre', form);
  const organismeHomologueId = Form.useWatch('organismeHomologueTrackingId', form);
  const paysEmetteurId = Form.useWatch('paysEmetteurTrackingId', form);
  const organismeMembreId = Form.useWatch('organismeMembreTrackingId', form);

  const norm = (s) => (s == null ? '' : String(s).trim().toUpperCase());

  /* L'assureur principal est la compagnie qui a émis la Carte Brune : elle est
     donc toujours du pays émetteur du contrat, que l'étape Contexte a déjà
     déduit du type de sinistre.
       ET (SURVENU_TOGO)     → émetteur = pays étranger → compagnies de ce pays
       TE (SURVENU_ETRANGER) → émetteur = Togo          → compagnies togolaises
     Le bureau homologue ne sert que de repli quand le pays émetteur n'est pas
     encore connu (formulaire ouvert hors parcours complet). */
  const bureauHomologue = organismes.find(o => o.organismeTrackingId === organismeHomologueId);
  const paysEmetteur = pays.find(p => p.paysTrackingId === paysEmetteurId);
  const cible = paysEmetteur
    ? { iso: norm(paysEmetteur.codeIso), bcb: norm(paysEmetteur.codeCarteBrune), libelle: paysEmetteur.libelle }
    : bureauHomologue
      ? { iso: norm(bureauHomologue.codePays), bcb: norm(bureauHomologue.codePaysBCB), libelle: bureauHomologue.raisonSociale }
      : null;

  const toutesCompagnies = organismes.filter(o => o.typeOrganisme === 'COMPAGNIE_MEMBRE');
  const compagniesDuPays = cible
    ? toutesCompagnies.filter(o => {
      const isoO = norm(o.codePays);
      const bcbO = norm(o.codePaysBCB);
      const matchIso = cible.iso && isoO && (cible.iso === isoO || cible.iso.startsWith(isoO) || isoO.startsWith(cible.iso));
      const matchBcb = cible.bcb && bcbO && cible.bcb === bcbO;
      return matchIso || matchBcb;
    })
    : [];

  /* Un filtre qui ne remonte rien bloquerait la déclaration (codes pays absents
     du paramétrage Organismes) : dans ce cas on rouvre la liste complète. */
  const filtrageActif = compagniesDuPays.length > 0;
  const compagniesRetenues = filtrageActif ? compagniesDuPays : toutesCompagnies;
  /* La compagnie déjà choisie - notamment celle posée par le lookup attestation -
     reste toujours sélectionnable, même si elle sort du filtre. */
  const dejaChoisie = toutesCompagnies.find(o => o.organismeTrackingId === organismeMembreId);
  const compagniesMembres = [
    ...compagniesRetenues,
    ...(dejaChoisie && !compagniesRetenues.includes(dejaChoisie) ? [dejaChoisie] : []),
  ].sort((a, b) => (a.raisonSociale || '').localeCompare(b.raisonSociale || ''));

  /* Le pays fait partie de l'identite de la compagnie, pas d'une info de confort :
     plusieurs compagnies portent le meme nom d'un pays a l'autre (« CORIS » au
     Burkina et au Niger). Sans lui, deux lignes voisines sont indistinguables et
     le choix se fait a pile ou face - d'ou un numero de sinistre errone, puisque
     c'est le code de cette compagnie qui y figure.
     Le libelle est un noeud React (pays aligne a droite, en retrait) : `label`
     n'est donc plus filtrable tel quel, et la recherche passe par `filterOption`
     sur un `recherche` qui couvre raison sociale, code ET pays. */
  const optionsPrincipal = compagniesMembres.map(o => {
    const pays = libellePaysOrganisme(o);
    const nom = o.code ? `${o.raisonSociale} (${o.code})` : o.raisonSociale;
    return {
      value: o.organismeTrackingId,
      recherche: `${nom} ${pays} ${o.codePays || ''} ${o.codePaysBCB || ''}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</span>
          {pays && (
            <span style={{ flexShrink: 0, fontSize: 12, color: T.inkMuted }}>{pays}</span>
          )}
        </div>
      ),
    };
  });

  /* Lookup attestation → Assuré existant */
  const lookupAttestation = async (numero) => {
    const value = (numero || '').trim();
    if (!value || value.length < 4) { setFound(false); return; }
    setSearching(true);
    try {
      const res = await axiosInstance.get(`/v1/assures/attestation/${encodeURIComponent(value)}`);
      const a = res.data?.data;
      if (a) {
        /* L'assuré retrouvé est désormais MIS À JOUR à l'envoi avec le contenu du
           formulaire (voir DeclarationSinistre). Deux conséquences :
             - tous ses champs sont repris, pas seulement l'identité - sinon la mise à
               jour effacerait profession, visite technique, remorque… qu'on n'aurait
               pas rechargés ;
             - une valeur absente chez l'assuré n'écrase pas ce que l'agent a déjà saisi
               (le n° de police tapé AVANT l'attestation était remis à vide). */
        const repris = {
          assureNom: a.nomAssure,
          assurePrenom: a.prenomAssure,
          assureTelephone: a.telephone,
          assureAdresse: a.adresse,
          numeroPolice: a.numeroPolice,
          numeroCGrise: a.numeroCGrise,
          immatriculation: a.immatriculation,
          marqueVehicule: a.marqueVehicule,
          proprietaireVehicule: a.proprietaireVehicule,
          organismeMembreTrackingId: a.organismeTrackingId,
          assureProfession: a.profession,
          prochaineVT: a.prochaineVT ? dayjs(a.prochaineVT) : null,
          capaciteVehicule: a.capaciteVehicule,
          nbPersonnesABord: a.nbPersonnesABord,
          remorqueImmatriculation: a.remorqueImmatriculation,
          remorqueMarque: a.remorqueMarque,
          remorqueModele: a.remorqueModele,
          remorqueAssureur: a.remorqueAssureur,
        };
        form.setFieldsValue({
          ...Object.fromEntries(Object.entries(repris).filter(([, val]) => val != null && val !== '')),
          assureTrackingIdExistant: a.assureTrackingId ?? undefined,
          estPersonneMorale: !!a.estPersonneMorale,
          aRemorque: !!a.aRemorque,
        });
        setFound(true);
      } else {
        setFound(false);
        form.setFieldValue('assureTrackingIdExistant', undefined);
      }
    } catch {
      setFound(false);
      form.setFieldValue('assureTrackingIdExistant', undefined);
    } finally {
      setSearching(false);
    }
  };

  const Wrapper = embedded ? React.Fragment : 'div';
  const wrapperProps = embedded ? {} : { style: { maxWidth: 760 } };

  return (
    <Wrapper {...wrapperProps}>
      {!embedded && (
        <>
          <h1 style={H_TITLE}>{t('contratAssureurs.titre')}</h1>
        </>
      )}

      {/* Hidden : assureTrackingId d'un assuré déjà enregistré (auto-set par le lookup) */}
      <Form.Item name="assureTrackingIdExistant" hidden><Input /></Form.Item>

      {/* ══════ Références contrat ══════ */}
      <div style={{ marginTop: embedded ? 0 : 40 }}>
        <div style={SECTION_LABEL}>{t('contratAssureurs.referencesContrat')}</div>

        {/* Le switch occupe la colonne de droite, directement au-dessus du champ
            N° d'attestation dont il conditionne l'obligation. La cellule gauche vide
            conserve l'alignement des deux colonnes. */}
        <Grid2>
          <div />
          <Field
            name="attestationIllisible"
            label={t('contratAssureurs.attestationIllisible')}
          >
            <Form.Item name="attestationIllisible" valuePropName="checked" noStyle>
              <Switch checkedChildren={t('contratAssureurs.illisible')} unCheckedChildren={t('contratAssureurs.lisible')} />
            </Form.Item>
          </Field>
        </Grid2>

        <Grid2>
          <Field
            name="numeroPolice"
            label={t('contratAssureurs.numeroPolice')}
            rules={[{ required: true, message: t('contratAssureurs.numeroPoliceRequis') }]}
          >
            <Input {...inputProps} placeholder={t('contratAssureurs.numeroPoliceExemple')} maxLength={50} />
          </Field>

          <Field
            name="numeroAttestation"
            label={t('contratAssureurs.numeroAttestation')}
            rules={attestationIllisible ? [] : [{ required: true, message: t('contratAssureurs.numeroAttestationRequis') }]}
          >
            <Input
              {...inputProps}
              placeholder={attestationIllisible ? t('contratAssureurs.illisible') : t('contratAssureurs.numeroAttestationExemple')}
              maxLength={30}
              disabled={attestationIllisible}
              suffix={
                searching
                  ? <Spin size="small" />
                  : found
                    ? <Tooltip title={t('contratAssureurs.attestationTrouvee')}>
                      <CheckCircleFilled style={{ color: T.brand }} />
                    </Tooltip>
                    : <SearchOutlined style={{ color: T.inkLight }} />
              }
              onBlur={(e) => { if (!attestationIllisible) lookupAttestation(e.target.value); }}
            />
          </Field>
        </Grid2>

        <Grid3>
          <Field
            name="dateEffet"
            label={t('contratAssureurs.dateEffet')}
          >
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
            />
          </Field>

          <Field
            name="dateEcheance"
            label={t('contratAssureurs.dateEcheance')}
          >
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
              disabledDate={(d) => {
                const effet = form.getFieldValue('dateEffet');
                return effet && d && d.isBefore(dayjs(effet), 'day');
              }}
            />
          </Field>

          <Field name="numeroCGrise" label={t('contratAssureurs.numeroCarteGrise')}>
            <Input {...inputProps} placeholder={t('contratAssureurs.numeroCarteGrise')} maxLength={50} />
          </Field>
        </Grid3>
      </div>

      {/* ══════ Assureurs ══════ */}
      <div style={{ marginTop: 28 }}>
        <div style={SECTION_LABEL}>
          {t('contratAssureurs.assureurs')}
          {filtrageActif && (
            <Tag color="green" style={{ marginLeft: 10, borderRadius: 12, fontSize: 11 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              {t('contratAssureurs.filtresSur', { libelle: cible.libelle })}
            </Tag>
          )}
        </div>

        <Field
          name="organismeMembreTrackingId"
          label={t('contratAssureurs.assureurPrincipal')}
          hint={
            typeSinistre === 'SURVENU_ETRANGER'
              ? t('contratAssureurs.assureurPrincipalIndicationTe')
              : t('contratAssureurs.assureurPrincipalIndicationEt')
          }
          rules={[{ required: true, message: t('contratAssureurs.assureurPrincipalRequis') }]}
        >
          <Select
            {...selectProps}
            showSearch
            placeholder={filtrageActif ? t('contratAssureurs.selectionnerCompagniePays', { libelle: cible.libelle }) : t('contratAssureurs.selectionnerCompagnieMembre')}
            filterOption={(saisie, option) =>
              (option?.recherche || '').toLowerCase().includes(saisie.toLowerCase())}
            notFoundContent={t('contratAssureurs.aucuneCompagnie')}
            options={optionsPrincipal}
          />
        </Field>

        {/* Sous la compagnie qu'il identifie : à l'étape Contexte, il se lisait comme
            une référence de plus, sans dire de quel assureur il s'agissait. L'assureur
            émetteur de l'attestation n'est plus saisi : c'est l'assureur principal. */}
        <Field
          name="numeroSinistreAssureur"
          label={t('contratAssureurs.numeroSinistreAssureur')}
          hint={t('contratAssureurs.numeroSinistreAssureurIndication')}
        >
          <Input {...inputProps} placeholder={t('contratAssureurs.numeroSinistreAssureurExemple')} maxLength={100} />
        </Field>

      </div>
    </Wrapper>
  );
}
